import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, useMotionValue, animate } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Sprout, ArrowUpRight, ArrowRight, Bell, Target, Share2,
  Users, Leaf, CalendarClock, Check,
} from "lucide-react";
import useLenis from "@/hooks/useLenis";
import api from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const HERO_IMG =
  "https://images.unsplash.com/photo-1659822887922-c1386185cc6b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&h=1600&fit=crop";
const IMG_A =
  "https://images.unsplash.com/photo-1550989460-0adf9ea622e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100&fit=crop";
const IMG_B =
  "https://images.unsplash.com/photo-1553279768-865429fa0078?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100&fit=crop";

const EASE = [0.22, 1, 0.36, 1];

/* ---------- motion helpers ---------- */
function MaskLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden pb-[0.12em]">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.05, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Reveal({ children, delay = 0, y = 28, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

function CountUp({ to, decimals = 0, suffix = "", prefix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const mv = useMotionValue(0);
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, to, { duration: 1.8, ease: EASE });
    const unsub = mv.on("change", (v) => setVal(v));
    return () => { controls.stop(); unsub(); };
  }, [inView, to, mv]);
  return (
    <span ref={ref}>
      {prefix}
      {val.toLocaleString("fr-FR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
      {suffix}
    </span>
  );
}

const MARQUEE = ["Moins de gaspillage", "Plus d'économies", "Zéro culpabilité", "Anti-gaspi", "Mangez à temps", "Sauvez la planète"];

/* ---------- page ---------- */
export default function Landing() {
  useLenis();
  const { t } = useTranslation();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "26%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  useEffect(() => {
    const key = "ff_visit_tracked";
    if (sessionStorage.getItem(key)) return;
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref") || params.get("r") || null;
    api.post("/track/visit", {
      path: window.location.pathname + window.location.search,
      ref,
      referrer: document.referrer || "",
    }).then(() => sessionStorage.setItem(key, "1")).catch(() => {});
  }, []);

  return (
    <div className="bg-background text-foreground overflow-x-hidden">
      {/* NAV */}
      <nav className="fixed top-0 inset-x-0 z-50 glass border-b border-border/60 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 sm:h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sprout className="h-6 w-6 text-primary" />
            <span className="font-display font-extrabold text-lg sm:text-xl tracking-tight">FoodFend</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3">
            <LanguageSwitcher compact />
            <Link to="/connexion" data-testid="nav-login"
              className="hidden sm:inline-flex text-sm font-semibold px-4 py-2 rounded-full hover:bg-secondary transition-colors">
              {t("nav.login")}
            </Link>
            <Link to="/connexion" data-testid="nav-cta"
              className="group text-sm font-semibold px-4 py-2 rounded-full bg-primary text-primary-foreground inline-flex items-center gap-1.5 transition-transform hover:-translate-y-0.5 min-h-[40px]">
              {t("nav.start")} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header ref={heroRef} className="relative min-h-[92vh] lg:min-h-screen flex flex-col justify-end pt-24 sm:pt-28 pb-10 sm:pb-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-8 sm:gap-10 items-end">
          {/* hero visual — moved above text on mobile */}
          <div className="order-1 lg:order-2 lg:col-span-5 relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.2, ease: EASE, delay: 0.35 }}
              className="relative rounded-[24px] sm:rounded-[28px] overflow-hidden aspect-[16/10] sm:aspect-[4/3] lg:aspect-[3/4] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.35)] grain-overlay">
              <motion.img
                src={HERO_IMG} alt="Panier de légumes frais"
                style={{ y: imgY, scale: imgScale }}
                className="absolute inset-0 h-[130%] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7 }}
                className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4 glass rounded-2xl p-3 sm:p-4 flex items-center justify-between">
                <div>
                  <p className="mono text-[10px] uppercase tracking-mono text-muted-foreground">{t("landing.savedThisMonth")}</p>
                  <p className="font-display font-extrabold text-xl sm:text-2xl text-primary">
                    <CountUp to={47} decimals={0} suffix=" €" />
                  </p>
                </div>
                <Leaf className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
              </motion.div>
            </motion.div>
          </div>

          <motion.div style={{ y: heroTextY }} className="order-2 lg:order-1 lg:col-span-7 relative z-10">
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}
              className="mono text-[10px] sm:text-xs tracking-mono uppercase text-primary mb-4 sm:mb-6">
              {t("landing.kicker")}
            </motion.p>
            <h1 className="font-display font-extrabold leading-[0.95] tracking-tight text-[clamp(2.4rem,10vw,6.5rem)]">
              <MaskLine delay={0.05}>{t("landing.hero1")}</MaskLine>
              <MaskLine delay={0.15}>{t("landing.hero2")}</MaskLine>
              <MaskLine delay={0.25}><span className="text-primary italic">{t("landing.hero3")}</span></MaskLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.8, ease: EASE }}
              className="mt-5 sm:mt-7 max-w-md text-[15px] sm:text-lg text-muted-foreground">
              {t("landing.heroSub")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8, ease: EASE }}
              className="mt-7 sm:mt-9 flex flex-wrap items-center gap-3">
              <Link to="/connexion" data-testid="hero-cta"
                className="group px-6 py-3.5 rounded-full bg-primary text-primary-foreground font-semibold inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5 min-h-[48px]">
                {t("nav.startFree")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/connexion"
                className="px-6 py-3.5 rounded-full border border-border font-semibold hover:bg-secondary transition-colors min-h-[48px] inline-flex items-center">
                {t("nav.demo")}
              </Link>
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 1 }}
          className="max-w-7xl mx-auto px-5 sm:px-8 w-full mt-12 hidden sm:flex items-center gap-3 text-muted-foreground">
          <span className="mono text-[11px] tracking-mono uppercase">{t("landing.scroll")}</span>
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }} className="h-4 w-px bg-primary" />
        </motion.div>
      </header>

      {/* MARQUEE */}
      <section aria-hidden className="border-y border-border py-5 sm:py-6 bg-primary text-primary-foreground overflow-hidden">
        <div className="marquee-track flex whitespace-nowrap">
          {[...MARQUEE, ...MARQUEE].map((w, i) => (
            <span key={i} className="font-display font-extrabold text-xl sm:text-2xl lg:text-4xl mx-6 sm:mx-8 inline-flex items-center gap-6 sm:gap-8">
              {w} <Sprout className="h-5 w-5 sm:h-6 sm:w-6 opacity-70" />
            </span>
          ))}
        </div>
      </section>

      {/* MANIFESTO — dark */}
      <section className="bg-[#0A120C] text-white py-20 sm:py-28 lg:py-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="mono text-xs tracking-mono uppercase text-primary mb-10 sm:mb-16">{t("landing.manifesto")}</p>
          </Reveal>
          {[
            { n: "01", t: t("landing.step1Title"), d: t("landing.step1Desc") },
            { n: "02", t: t("landing.step2Title"), d: t("landing.step2Desc") },
            { n: "03", t: t("landing.step3Title"), d: t("landing.step3Desc") },
          ].map((c, i) => (
            <div key={c.n} className={`grid md:grid-cols-12 gap-4 md:gap-10 items-start py-8 sm:py-10 ${i > 0 ? "border-t border-white/10" : ""}`}>
              <Reveal delay={0.05} className="md:col-span-3">
                <span className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl text-primary/90">{c.n}</span>
              </Reveal>
              <Reveal delay={0.1} className="md:col-span-4">
                <h3 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl">{c.t}</h3>
              </Reveal>
              <Reveal delay={0.15} className="md:col-span-5">
                <p className="text-white/60 text-base sm:text-lg leading-relaxed">{c.d}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 sm:py-28 lg:py-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="mono text-xs tracking-mono uppercase text-primary mb-4">{t("landing.featuresKicker")}</p>
            <h2 className="font-display font-extrabold text-[clamp(2rem,7vw,4rem)] sm:text-6xl tracking-tight max-w-3xl leading-[0.95]">
              {t("landing.featuresTitle")}
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mt-10 sm:mt-16">
            {[
              { icon: CalendarClock, t: t("landing.featUrgencyT"), d: t("landing.featUrgencyD") },
              { icon: Bell, t: t("landing.featRemindT"), d: t("landing.featRemindD") },
              { icon: Target, t: t("landing.featGoalT"), d: t("landing.featGoalD") },
              { icon: Share2, t: t("landing.featBadgeT"), d: t("landing.featBadgeD") },
              { icon: Users, t: t("landing.featRefT"), d: t("landing.featRefD") },
              { icon: Leaf, t: t("landing.featImpactT"), d: t("landing.featImpactD") },
            ].map((f, i) => (
              <Reveal key={f.t} delay={(i % 3) * 0.08}>
                <div className="group h-full rounded-[24px] border border-border p-7 card-hover bg-card">
                  <div className="h-12 w-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-6 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <f.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-display font-bold text-xl mb-2">{f.t}</h3>
                  <p className="text-muted-foreground">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PHOTOGRAPHY + IMPACT */}
      <section className="pb-20 sm:pb-28 lg:pb-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-5 sm:gap-6 items-stretch">
          <Reveal className="relative">
            <div className="rounded-[24px] sm:rounded-[28px] overflow-hidden h-full min-h-[240px] sm:min-h-[360px] relative">
              <img src={IMG_A} alt="Table de produits frais" loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-[filter,transform] duration-700 grayscale hover:grayscale-0 hover:scale-105" />
            </div>
          </Reveal>
          <div className="grid gap-5 sm:gap-6">
            <Reveal delay={0.08}>
              <div className="rounded-[24px] sm:rounded-[28px] bg-[#0A120C] text-white p-6 sm:p-10">
                <p className="mono text-xs tracking-mono uppercase text-primary mb-6 sm:mb-8">{t("landing.impactKicker")}</p>
                <div className="grid grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <p className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl"><CountUp to={2500} suffix="+" /></p>
                    <p className="text-white/50 text-xs sm:text-sm mt-1">{t("landing.impactSaved")}</p>
                  </div>
                  <div>
                    <p className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-primary"><CountUp to={6} prefix="" suffix="k€" /></p>
                    <p className="text-white/50 text-xs sm:text-sm mt-1">{t("landing.impactMoney")}</p>
                  </div>
                  <div>
                    <p className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl"><CountUp to={5} suffix="t" /></p>
                    <p className="text-white/50 text-xs sm:text-sm mt-1">{t("landing.impactCo2")}</p>
                  </div>
                </div>
                <ul className="mt-6 sm:mt-8 space-y-2.5 sm:space-y-3">
                  {[t("landing.impactFree"), t("landing.impactPrivate"), t("landing.impactNoCard")].map((x) => (
                    <li key={x} className="flex items-center gap-3 text-white/80 text-sm sm:text-base">
                      <span className="h-6 w-6 rounded-full bg-primary/20 text-primary flex items-center justify-center shrink-0">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="rounded-[24px] sm:rounded-[28px] overflow-hidden h-[180px] sm:h-[220px] relative">
                <img src={IMG_B} alt="Bol de salade fraîche" loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative bg-primary text-primary-foreground py-20 sm:py-28 lg:py-40 overflow-hidden">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center relative z-10">
          <Reveal>
            <h2 className="font-display font-extrabold text-[clamp(2rem,8vw,5.5rem)] leading-[0.95] tracking-tight">
              {t("landing.ctaTitle")}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 sm:mt-6 text-base sm:text-lg text-primary-foreground/80 max-w-xl mx-auto">
              {t("landing.ctaSub")}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/connexion" data-testid="final-cta"
              className="group mt-8 sm:mt-10 inline-flex items-center gap-2 bg-white text-[#0A120C] font-semibold px-7 sm:px-8 py-3.5 sm:py-4 rounded-full transition-transform hover:-translate-y-1 min-h-[48px]">
              {t("landing.ctaBtn")}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="absolute -bottom-24 -right-16 opacity-10">
          <Sprout className="h-[240px] w-[240px] sm:h-[320px] sm:w-[320px]" />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0A120C] text-white py-14">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <Sprout className="h-6 w-6 text-primary" />
            <span className="font-display font-extrabold text-xl">FoodFend</span>
          </div>
          <p className="mono text-xs text-white/40 tracking-mono uppercase">{t("landing.footerTagline")}</p>
          <Link to="/connexion" className="text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1">
            {t("nav.login")} <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </footer>
    </div>
  );
}
